import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeConversions.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { RefreshCw, AlertCircle, MousePointerClick } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeKpiCard from '../components/MicrositeKpiCard/MicrositeKpiCard'
import MicrositeConversionTable from '../components/MicrositeConversionTable/MicrositeConversionTable'

const RANGE_OPTIONS = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' }
]

const formatDate = (d) => d.toISOString().slice(0, 10)

const getDateRange = (daysAgo) => {
    const toDate = new Date()
    const fromDate = new Date()
    fromDate.setDate(fromDate.getDate() - Number(daysAgo))
    return { fromDate: formatDate(fromDate), toDate: formatDate(toDate) }
}

/**
 * MicrositeConversions
 * Read-only dashboard of conversion events (popup clicks, form
 * submissions, booking CTAs, etc.) — pulled from the real
 * micrositeGetConversionStats endpoint, which groups by eventType
 * and returns { eventType, count, topPage } rows, plus a single
 * totalEvents figure for the date range. There is no separate
 * "conversion rate" or "goals" concept in the API contract —
 * eventType IS the goal, and count is its performance.
 */
const MicrositeConversions = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [rangeDays, setRangeDays] = useState('30')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [totalEvents, setTotalEvents] = useState(0)
    const [rows, setRows] = useState([])

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadConversions = useCallback(async (daysAgo) => {
        setLoading(true)
        setError(false)

        const { fromDate, toDate } = getDateRange(daysAgo)
        const res = await post('micrositeGetConversionStats', { fromDate, toDate })

        if (res?.status === 'success') {
            setTotalEvents(res.response?.totalEvents ?? 0)
            setRows(res.response?.data ?? [])
        } else {
            setError(true)
        }

        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadConversions(rangeDays)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, rangeDays])

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading conversions…</span>
            </div>
        )
    }

    if (error) {
        return (
            <div className={styles.errorWrap}>
                <AlertCircle size={18} />
                <span>Could not load conversions. Please refresh.</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <MicrositePageHeader
                title="Conversions"
                subtitle="Track how well your site turns visitors into bookings and leads."
            />

            <div className={styles.rangeRow}>
                <SelectInput value={rangeDays} onChange={setRangeDays} options={RANGE_OPTIONS} />
            </div>

            <div className={styles.kpiGrid}>
                <MicrositeKpiCard icon={MousePointerClick} label="Total conversion events" value={totalEvents} />
            </div>

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Events by type</h2>
                <p className={styles.groupDesc}>Each conversion event recorded via micrositeRecordConversionEvent, grouped by type.</p>
                <MicrositeConversionTable rows={rows} />
            </section>
        </div>
    )
}

export default MicrositeConversions